/**
 * Central tuning + quality configuration.
 * Subsystems read from here rather than hardcoding magic numbers, so the
 * quality scaler and the capture harness can drive everything from one place.
 */

export const PHYSICS_HZ = 120;
export const FIXED_DT = 1 / PHYSICS_HZ;
/** Never simulate more than this many physics steps in one frame (spiral-of-death guard). */
export const MAX_SUBSTEPS = 8;

/** Real-world units are metres, seconds, kilograms. */
export const UNITS = {
  gravity: -9.81 * 2.1, // Games use exaggerated gravity; CoD-like feel.
  playerHeight: 1.78,
  playerCrouchHeight: 1.12,
  playerRadius: 0.32,
  eyeOffset: 0.12, // below top of capsule
};

export const QUALITY_PRESETS = {
  low: {
    // This is also the recovery profile for integrated/low-memory GPUs. Keep
    // both the browser drawing buffer and the HDR pipeline bounded: lowering
    // only renderScale still leaves a full-DPR canvas and can exhaust VRAM.
    renderScale: 0.62,
    maxPixelRatio: 1,
    maxDisplayPixels: 1_400_000,
    materialScale: 0.25,
    aiTextureSize: 256,
    fxAtlasSize: 256,
    // Safe mode must avoid the expensive shader families too, not just render
    // them into smaller targets. On Vega 3 the old "low" preset still spent
    // several minutes baking surfaces and compiling CSM/G-buffer variants.
    proceduralMaterials: false,
    advancedLighting: false,
    shadows: false,
    prepass: false,
    ai: false,
    shadowMapSize: 256,
    cascades: 1,
    shadowDistance: 48,
    taa: false,
    gtao: false,
    ssr: false,
    volumetrics: false,
    atmosphericFog: false,
    motionBlur: false,
    bloom: false,
    anisotropy: 2,
    particleBudget: 1000,
    decalBudget: 32,
  },
  medium: {
    renderScale: 0.85,
    maxPixelRatio: 1.25,
    maxDisplayPixels: 2_200_000,
    materialScale: 0.5,
    aiTextureSize: 384,
    fxAtlasSize: 512,
    shadowMapSize: 2048,
    cascades: 3,
    shadowDistance: 90,
    taa: true,
    gtao: true,
    ssr: false,
    volumetrics: true,
    motionBlur: true,
    bloom: true,
    anisotropy: 8,
    particleBudget: 6000,
    decalBudget: 128,
  },
  high: {
    renderScale: 1.0,
    maxPixelRatio: 1.5,
    maxDisplayPixels: 3_400_000,
    materialScale: 0.75,
    aiTextureSize: 512,
    fxAtlasSize: 1024,
    shadowMapSize: 2048,
    cascades: 4,
    shadowDistance: 140,
    taa: true,
    gtao: true,
    ssr: true,
    volumetrics: true,
    motionBlur: true,
    bloom: true,
    anisotropy: 16,
    particleBudget: 12000,
    decalBudget: 256,
  },
  ultra: {
    renderScale: 1.0,
    maxPixelRatio: 1.5,
    maxDisplayPixels: 5_000_000,
    materialScale: 1,
    aiTextureSize: 512,
    fxAtlasSize: 1024,
    shadowMapSize: 4096,
    cascades: 4,
    shadowDistance: 200,
    taa: true,
    gtao: true,
    ssr: true,
    volumetrics: true,
    motionBlur: true,
    bloom: true,
    anisotropy: 16,
    particleBudget: 24000,
    decalBudget: 512,
  },
};

export const DEFAULTS = {
  quality: 'ultra',
  fov: 80, // horizontal-ish vertical FOV, CoD default feel
  adsFovScale: 0.72,
  sensitivity: 0.0022,
  adsSensScale: 0.65,
  invertY: false,
  // Match Fracture Protocol's controller response and worn-stick tolerance.
  padLookSpeed: 1.0,
  padDeadzone: 0.14,
  rumble: true,
  exposure: 1.0,
  /** Capture mode disables anything nondeterministic so screenshots are stable. */
  deterministic: false,
  /** Expensive shader compilation is opt-in during interactive play. */
  prewarmShaders: false,
};

export function createConfig(overrides = {}) {
  const cfg = { ...DEFAULTS, ...overrides };
  if (!QUALITY_PRESETS[cfg.quality]) {
    console.warn(`[config] unknown quality preset "${cfg.quality}"; using "${DEFAULTS.quality}"`);
    cfg.quality = DEFAULTS.quality;
  }
  cfg.q = { ...QUALITY_PRESETS[cfg.quality] };
  cfg.setQuality = (name) => {
    if (!QUALITY_PRESETS[name]) throw new Error(`unknown quality preset "${name}"`);
    cfg.quality = name;
    Object.assign(cfg.q, QUALITY_PRESETS[name]);
  };
  return cfg;
}
